import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { frontendDisplayedCourseSelector } from '@/atoms/auth'
import { viewingStateAtom, ViewingStateType } from '@/atoms/viewingState'
import { getOutlineNoteSummaries } from '@/api/cb/contentCreatorContent'
import { OutlineNoteSummary } from 'common/src/api/contentFrontendVsContentBackend/contentCreatorNotes'
import styles from './ContentCreatorNotes.module.less'

interface OutlineNoteSummaryWithOrder extends OutlineNoteSummary {
    outlineOrder: number
}

export const OutlineNotesTable: React.FC<unknown> = (): ReactElement => {
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const setViewingState = useSetRecoilState(viewingStateAtom)
    const [rawSummaries, setRawSummaries] = useState<OutlineNoteSummary[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (!courseName) return
        setLoading(true)
        getOutlineNoteSummaries(courseName).then((response) => {
            if (!response.data.isError) {
                setRawSummaries(response.data.payload.summaries)
            }
            setLoading(false)
        })
    }, [courseName])

    const summaries = useMemo(
        (): OutlineNoteSummaryWithOrder[] =>
            rawSummaries.map((s, i) => ({ ...s, outlineOrder: i })),
        [rawSummaries]
    )

    const navigateToOutline = useCallback(
        (summary: OutlineNoteSummary): void => {
            setViewingState({
                contentPath: summary.contentPath,
                viewingStateType: ViewingStateType.outline,
                id: null,
            })
        },
        [setViewingState]
    )

    const allSources = [
        ...new Set(summaries.map((s) => s.source).filter(Boolean) as string[]),
    ].sort()

    const columns: ColumnsType<OutlineNoteSummaryWithOrder> = [
        {
            title: 'Section',
            dataIndex: 'sectionName',
            key: 'sectionName',
            width: 250,
            sorter: (a, b) => a.outlineOrder - b.outlineOrder,
        },
        {
            title: 'Source',
            dataIndex: 'source',
            key: 'source',
            width: 150,
            filters: allSources.map((s) => ({ text: s, value: s })),
            onFilter: (value, record) => record.source === value,
            render: (val: string) => val || '-',
        },
        {
            title: 'Last Updated',
            dataIndex: 'lastUpdated',
            key: 'lastUpdated',
            width: 150,
            sorter: (a, b) => a.lastUpdated - b.lastUpdated,
            render: (val: number) =>
                val ? new Date(val).toLocaleDateString() : '-',
        },
        {
            title: 'My Notes',
            dataIndex: 'myNotes',
            key: 'myNotes',
            width: 300,
            ellipsis: true,
            render: (val: string) =>
                val ? (
                    <Tooltip
                        title={
                            <span style={{ whiteSpace: 'pre-wrap' }}>
                                {val}
                            </span>
                        }
                        placement="topLeft"
                        overlayStyle={{ maxWidth: 500 }}
                    >
                        <span className={styles.notesPreview}>{val}</span>
                    </Tooltip>
                ) : (
                    '-'
                ),
        },
        {
            title: 'Link',
            key: 'link',
            width: 60,
            render: (_: unknown, record: OutlineNoteSummary) => (
                <a
                    className={styles.linkButton}
                    onClick={() => navigateToOutline(record)}
                >
                    View
                </a>
            ),
        },
    ]

    return (
        <div className={styles.contentCreatorNotesTable}>
            <div className={styles.title}>Outline Notes</div>
            <Table
                columns={columns}
                dataSource={summaries}
                rowKey="hashedContentPath"
                loading={loading}
                size="small"
                pagination={{ pageSize: 50, showSizeChanger: true }}
                scroll={{ x: 900 }}
            />
        </div>
    )
}
